/**
 * Rhythmz — AudioSourceManager
 * ──────────────────────────────
 * Manages the 5 audio input sources, all feeding a single AnalyserNode-free
 * graph straight into Butterchurn. Handles the AudioContext lifecycle, autoplay
 * policy (resume on user gesture), and clean teardown between sources.
 *
 * Sources:
 *   demo     — built-in demo tracks
 *   file     — drag & drop / file picker
 *   mic      — live microphone
 *   desktop  — system/tab audio via getDisplayMedia (Chrome/Edge)
 *   url      — remote stream URL (CORS permitting)
 */

export type SourceKind = 'demo' | 'file' | 'mic' | 'desktop' | 'url';

export interface SourceState {
  kind: SourceKind | null;
  label: string;
  playing: boolean;
}

export class AudioSourceManager {
  readonly ctx: AudioContext;
  /** Master gain that every source connects through. Connect this to both
   *  Butterchurn (for visuals) and destination (so the user hears it). */
  readonly output: GainNode;

  private current: { kind: SourceKind; srcNode: AudioNode; stop: () => void } | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private onStateChange?: (s: SourceState) => void;

  constructor() {
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.output = this.ctx.createGain();
    this.output.gain.value = 1;
    this.output.connect(this.ctx.destination);
  }

  setStateListener(fn: (s: SourceState) => void): void {
    this.onStateChange = fn;
  }

  /** Resume the context — MUST be called from a user gesture (autoplay policy). */
  async resume(): Promise<void> {
    if (this.ctx.state !== 'running') await this.ctx.resume();
  }

  /** Connect a visualizer's audio input to the master output. */
  connectVisualizer(connectFn: (node: AudioNode) => void): void {
    connectFn(this.output);
  }

  /* ─────────────── Source switchers ─────────────── */

  async playDemo(url: string, title: string): Promise<void> {
    await this.resume();
    this.teardown();
    const el = new Audio();
    el.crossOrigin = 'anonymous';
    el.loop = true;
    el.src = url;
    this.audioEl = el;
    const src = this.ctx.createMediaElementSource(el);
    src.connect(this.output);
    await el.play().catch(() => {});
    this.current = {
      kind: 'demo',
      srcNode: src,
      stop: () => { el.pause(); el.src = ''; },
    };
    this.emit(title, true);
  }

  async playFile(file: File): Promise<void> {
    await this.resume();
    this.teardown();
    const el = new Audio();
    el.loop = true;
    el.src = URL.createObjectURL(file);
    this.audioEl = el;
    const src = this.ctx.createMediaElementSource(el);
    src.connect(this.output);
    await el.play().catch(() => {});
    this.current = {
      kind: 'file',
      srcNode: src,
      stop: () => { el.pause(); URL.revokeObjectURL(el.src); el.src = ''; },
    };
    this.emit(file.name, true);
  }

  async useMic(): Promise<void> {
    await this.resume();
    this.teardown();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    });
    const src = this.ctx.createMediaStreamSource(stream);
    // Mic: connect to visualizer only (NOT destination — would cause feedback).
    // We temporarily disconnect output from destination.
    src.connect(this.output);
    this.current = {
      kind: 'mic',
      srcNode: src,
      stop: () => {
        src.disconnect();
        stream.getTracks().forEach((t) => t.stop());
      },
    };
    this.emit('Microphone', true);
  }

  /** Desktop/system audio via screen capture with audio. Chrome/Edge only.
   *  The user picks a tab/screen to share audio from. */
  async useDesktop(): Promise<void> {
    await this.resume();
    this.teardown();
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { width: 2, height: 2 }, // minimal video required by API
      audio: {
        // @ts-ignore — these constraints are valid in Chrome/Edge
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      } as MediaTrackConstraints,
    });
    // Stop the video track immediately — we only want audio.
    stream.getVideoTracks().forEach((t) => t.stop());
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      this.emit('Desktop audio unavailable', false);
      throw new Error('No audio track — choose a tab/screen with "share audio" enabled');
    }
    const src = this.ctx.createMediaStreamSource(stream);
    src.connect(this.output);
    this.current = {
      kind: 'desktop',
      srcNode: src,
      stop: () => {
        src.disconnect();
        stream.getTracks().forEach((t) => t.stop());
      },
    };
    this.emit('Desktop / System Audio', true);
  }

  async playUrl(url: string): Promise<void> {
    await this.resume();
    this.teardown();
    const el = new Audio();
    el.crossOrigin = 'anonymous';
    el.loop = true;
    el.src = url;
    this.audioEl = el;
    const src = this.ctx.createMediaElementSource(el);
    src.connect(this.output);
    await el.play().catch(() => {
      throw new Error('URL blocked (CORS) or invalid');
    });
    this.current = {
      kind: 'url',
      srcNode: src,
      stop: () => { el.pause(); el.src = ''; },
    };
    this.emit(url.split('/').pop() ?? url, true);
  }

  /* ─────────────── Transport ─────────────── */

  togglePlay(): void {
    if (this.audioEl) {
      if (this.audioEl.paused) void this.audioEl.play();
      else this.audioEl.pause();
      this.emit(this.current?.kind === 'url' ? this.audioEl.src : (this.audioEl.title || 'audio'), !this.audioEl.paused);
    }
  }

  setVolume(v: number): void {
    this.output.gain.value = v;
  }

  get element(): HTMLAudioElement | null { return this.audioEl; }

  teardown(): void {
    this.current?.stop();
    this.current = null;
    this.audioEl = null;
  }

  private emit(label: string, playing: boolean): void {
    this.onStateChange?.({ kind: this.current?.kind ?? null, label, playing });
  }

  dispose(): void {
    this.teardown();
    void this.ctx.close();
  }
}
